import { FileUpload } from '@consigli/facade';
import { ExtendedFile, useGetLayoutOptimizationsQuery, useProjectId } from '@consigli/hooks';
import { JobType, LayoutType, PipelineName } from '@consigli/types';
import { useCallback, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IoIosInformationCircle } from 'react-icons/io';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';

import { IterationName } from '../components/iteration-name';
import { SelectLanguage } from '../components/select-language';
import { UploadInfoPanel } from '../components/upload-info-panel';
import { useFetchValidateName } from '../components/user-input';
import { WizardAnalyzeButton } from '../components/wizard-analyze-button';
import { TabType, WizardLayout } from '../components/wizard-layout';
import { useCreatePackageUtil } from '../hooks/use-create-package';
import { useUploadProgress } from '../hooks/use-upload-progress';

export const SpaceAnalysisWizard = () => {
  const [formData] = useState<FieldValues>({
    iterationName: '',
    language: undefined,
  });

  const { t } = useTranslation();
  const [ifcFiles, setIfcFiles] = useState<ExtendedFile[]>([]);
  const [excelFiles, setExcelFiles] = useState<ExtendedFile[]>([]);
  const projectId = useProjectId();

  const handleIfcFilesChange = useCallback(
    (value: React.SetStateAction<File[]>) => {
      const newFiles = typeof value === 'function' ? value(ifcFiles) : value;

      if (newFiles.length < ifcFiles.length) {
        setIfcFiles(newFiles as ExtendedFile[]);
        return;
      }

      const addedFiles = newFiles.filter(
        (newFile) => !ifcFiles.some((existingFile) => existingFile.name === newFile.name),
      );

      const invalidFiles = addedFiles.filter((file) => !file.name.toLowerCase().endsWith('.ifc'));
      const validFiles = addedFiles.filter((file) => file.name.toLowerCase().endsWith('.ifc'));

      if (invalidFiles.length > 0) {
        if (invalidFiles.length === 1) {
          toast.error(`${invalidFiles[0].name} ${t('package-creation.ifc-file-invalid')}`);
        } else {
          toast.error(`${invalidFiles.length} ${t('package-creation.ifc-files-rejected')}`);
        }
      }

      setIfcFiles((prev) => [...prev, ...validFiles]);
    },
    [ifcFiles, t],
  );

  const handleExcelFilesChange = useCallback(
    (value: React.SetStateAction<File[]>) => {
      const newFiles = typeof value === 'function' ? value(excelFiles) : value;

      if (newFiles.length < excelFiles.length) {
        setExcelFiles(newFiles as ExtendedFile[]);
        return;
      }

      const addedFiles = newFiles.filter(
        (newFile) => !excelFiles.some((existingFile) => existingFile.name === newFile.name),
      );

      const invalidFiles = addedFiles.filter((file) => {
        const fileName = file.name.toLowerCase();
        return !(fileName.endsWith('.xlsx') || fileName.endsWith('.xls'));
      });

      const validFiles = addedFiles.filter((file) => {
        const fileName = file.name.toLowerCase();
        return fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
      });

      if (invalidFiles.length > 0) {
        if (invalidFiles.length === 1) {
          toast.error(`${invalidFiles[0].name} ${t('package-creation.excel-file-invalid')}`);
        } else {
          toast.error(`${invalidFiles.length} ${t('package-creation.excel-files-rejected')}`);
        }
      }

      setExcelFiles((prev) => [...prev, ...validFiles]);
    },
    [excelFiles, t],
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [targetCounter, setTargetCounter] = useState(0);
  const [currentCounter, setCurrentCounter] = useState(0);

  const { onAnalyze: onProcess, startAnalysis } = useCreatePackageUtil({
    setCurrentCounter,
    setTargetCounter,
  });

  const { isUploading, progress } = useUploadProgress(currentCounter, targetCounter);

  const methods = useForm<FieldValues>({
    defaultValues: formData,
  });

  const { getValues, handleSubmit } = methods;

  const onAnalyze = useCallback(async () => {
    if (ifcFiles.length === 0) {
      toast.error(t('package-creation.ifc-file-required'));
      return;
    }

    if (excelFiles.length === 0) {
      toast.error(t('package-creation.excel-file-required'));
      return;
    }

    try {
      setIsProcessing(true);

      const combinedFiles: (File & {
        isIfcFile?: boolean;
        isExcelFile?: boolean;
      })[] = [];

      const modifiedIfcFiles = ifcFiles.map((file) => {
        const extendedFile = file as File & { isIfcFile?: boolean };
        extendedFile.isIfcFile = true;
        return extendedFile;
      });
      combinedFiles.push(...modifiedIfcFiles);

      const modifiedExcelFiles = excelFiles.map((file) => {
        const extendedFile = file as File & { isExcelFile?: boolean };
        extendedFile.isExcelFile = true;
        return extendedFile;
      });
      combinedFiles.push(...modifiedExcelFiles);

      const response = await onProcess(combinedFiles, getValues(), LayoutType.SPACE_ANALYSIS);

      if (response) {
        await startAnalysis(response.id, PipelineName.SPACE_ANALYSIS, JobType.LAYOUT_OPTIMIZATION);
      }
    } catch (error) {
      console.log('Error starting analysis:', error);
      toast.error(t('package-creation.failed-analyzed'));
    } finally {
      setIsProcessing(false);
    }
  }, [getValues, onProcess, startAnalysis, t, ifcFiles, excelFiles]);

  const { validateName } = useFetchValidateName(useGetLayoutOptimizationsQuery, {
    projectId,
  });

  const CreateSpaceAnalysisPackageForm = () => {
    return (
      <>
        <FormProvider {...methods}>
          <form
            className="w-full max-w-[90rem] -mx-4 px-4 sm:mx-0 sm:px-0"
            onSubmit={handleSubmit(onAnalyze)}
          >
            <div className="flex flex-col max-w-[50rem] mx-auto" translate="no">
              <IterationName
                validateName={validateName}
                label={t('package-creation.analysis-name')}
                errorMessage={`${t('package-creation.analysis-name-empty')}*`}
              />
              <SelectLanguage />
            </div>

            {/* Two columns layout for IFC File and Excel File */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              {/* IFC File Upload - MANDATORY */}
              <div className="flex flex-col">
                <div className="mb-2 flex items-center gap-2">
                  <p className="text-base font-semibold text-day-neutral-subtle">
                    {t('package-creation.ifc-file-label')}{' '}
                    <span className="text-day-error-default">*</span>
                  </p>
                  <IoIosInformationCircle
                    size={20}
                    color="#7588a3"
                    data-tooltip-id="space-analysis-ifc-info"
                    className="cursor-help"
                  />
                </div>
                <Tooltip
                  id="space-analysis-ifc-info"
                  place="top"
                  opacity={100}
                  classNameArrow="hidden"
                  className="!bg-day-light-5 !p-4 !h-auto !w-80 !text-day-neutral-subtle !rounded-tl-[24px] !rounded-bl-[24px] !rounded-tr-[24px] !shadow-[0_1px_2px_0_rgba(0,0,0,0.06),0_1px_3px_0_rgba(0,0,0,0.10)] !z-10"
                >
                  <div className="text-sm whitespace-pre-line">
                    {t('package-creation.space-analysis-ifc-instructions')}
                  </div>
                </Tooltip>
                <div className="text-sm text-day-neutral-muted mb-1">
                  {t('package-creation.ifc-file-format')}
                </div>
                <FileUpload
                  files={ifcFiles}
                  setFiles={handleIfcFilesChange}
                  dropAreaLabel={t('document-upload.drag-n-drop-here')}
                  dropAreaClassName="mt-2 h-48 w-full"
                  fileButtonLabel={t('document-upload.browse-files')}
                  folderButtonLabel={t('document-upload.browse-folders')}
                  uploadListLabel={t('optimization-tabs.ifc-files')}
                  zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
                />
              </div>

              <div className="flex flex-col">
                <div className="mb-2 flex items-center gap-2">
                  <p className="text-base font-semibold text-day-neutral-subtle">
                    {t('package-creation.excel-file-label')}{' '}
                    <span className="text-day-error-default">*</span>
                  </p>
                  <IoIosInformationCircle
                    size={20}
                    color="#7588a3"
                    data-tooltip-id="space-analysis-excel-info"
                    className="cursor-help"
                  />
                </div>
                <Tooltip
                  id="space-analysis-excel-info"
                  place="top"
                  opacity={100}
                  classNameArrow="hidden"
                  className="!bg-day-light-5 !p-4 !h-auto !w-80 !text-day-neutral-subtle !rounded-tl-[24px] !rounded-bl-[24px] !rounded-tr-[24px] !shadow-[0_1px_2px_0_rgba(0,0,0,0.06),0_1px_3px_0_rgba(0,0,0,0.10)] !z-10"
                >
                  <div className="text-sm">
                    {t('package-creation.space-analysis-excel-instructions')}
                  </div>
                </Tooltip>
                <div className="text-sm text-day-neutral-muted mb-1">
                  {t('package-creation.excel-file-format')}
                </div>
                <FileUpload
                  files={excelFiles}
                  setFiles={handleExcelFilesChange}
                  dropAreaLabel={t('document-upload.drag-n-drop-here')}
                  dropAreaClassName="mt-2 h-48 w-full"
                  fileButtonLabel={t('document-upload.browse-files')}
                  folderButtonLabel={t('document-upload.browse-folders')}
                  uploadListLabel={t('document-upload.files')}
                  zeroByteFilesMessage={t('document-upload.file-zero-bytes')}
                />
              </div>
            </div>

            <div className="fc-w-full mt-6 max-w-[50rem] mx-auto">
              <WizardAnalyzeButton
                localFiles={[...ifcFiles, ...excelFiles]}
                type="submit"
                disabled={ifcFiles.length === 0 || excelFiles.length === 0}
              />
            </div>
          </form>
        </FormProvider>
      </>
    );
  };

  return (
    <WizardLayout
      isProcessing={isProcessing}
      messageHeader={<UploadInfoPanel />}
      isUploading={isUploading}
      progress={progress}
      tabContent={{
        [TabType.CREATE_PACKAGE]: <CreateSpaceAnalysisPackageForm />,
      }}
    />
  );
};
